import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import {
  getLocalStorage,
  setLocalStorage,
  setSkipRouteStorage,
  getNextSkipRoute,
  getSkipRouteStorage,
} from '../../utils'
import SlideContainer from '../../components/Layout/SlideContainer'
import LinkButton from '../../components/LinkButton'
import Breakpoints from '../../constants/breakpoints'
import Form from '../../components/Form'
import { ModalConsumer } from '../../context/ModalContext'
import QuestionComponents from '../../components/Question'
import {
  Question as QuestionType,
  FormItem as FormItemType,
  Answer,
  Slide,
  ModalLink,
} from '../../types'
import Readspeaker from '../../components/Readspeaker'

interface Props {
  routeId: string
  basePath: string
  title: string
  subtitle: string
  description: string
  descriptionAlign?: string
  button: string
  nextLink: string
  questions: QuestionType[]
  showTextArea?: boolean
  textAreaLabel?: string
  routeIndex?: string
  routes: Slide[]
  modalLink: string
  modalHeader: string
  modalText: string
  modalLinks?: ModalLink[]
  onSkipRoute: (path: string) => void
}

const findLocalStorageAnswer = (routeId: string) => {
  const foundAnswer = getLocalStorage('answers').find((answer) => answer.routeId === routeId)

  if (foundAnswer && foundAnswer.answer.length > 0) {
    return foundAnswer.answer
  }
}

const findLocalStorageExplanation = (routeId: string) => {
  const foundExplanation = getLocalStorage('answers').find(
    (explanation) => explanation.routeId === routeId,
  )

  if (foundExplanation && foundExplanation.answer.length > 0) {
    return foundExplanation.explanation
  }
}

const Question: React.FC<Props> = ({
  basePath,
  routeIndex,
  routes,
  onSkipRoute,
  questions,
  routeId,
  title,
  subtitle,
  description,
  descriptionAlign,
  showTextArea,
  textAreaLabel,
  button,
  nextLink,
  modalHeader,
  modalLink,
  modalText,
  modalLinks,
}) => {
  const selectedAnswers: Answer[] = []
  const localStorage = findLocalStorageAnswer(routeId) || []
  const formItems = questions
    .filter((question) => question.type !== 'multiple' || 'single')
    .map((item: QuestionType, index) => ({
      inputName: item.title || `textarea-${index}`,
      value: localStorage[index],
    }))

  useEffect(() => {
    if (routeIndex) {
      const nextRoute = getNextSkipRoute(routeIndex, routes)

      if (nextRoute) onSkipRoute(nextRoute.path)
    }
  }, [])

  // Filters out the matches between the answers from the api and the answers from the localStorage and then pushes those answers to the selectedAnswers array.
  questions.forEach(
    (question) =>
      question.answers &&
      question.answers.filter(
        (answer) =>
          answer &&
          localStorage.some((storedAnswer) => storedAnswer === answer.title) &&
          selectedAnswers.push(answer),
      ),
  )

  const explanation = findLocalStorageExplanation(routeId) || ''

  const [allFormItems, setAllFormItems] = useState(formItems)
  const [isConditionalRoute, setIsConditionalRoute] = useState(false)
  const [answers, setAnswers] = useState(selectedAnswers)
  const [explanationItem, setExplanationItem] = useState(explanation)

  useEffect(() => {
    handleLocalStorage()
    handleStoreSkipRoutes()
  }, [answers])

  const listOfSkippableRoutes = () => {
    const skipRoutes: string[] = []

    questions.forEach(
      (question) =>
        question.answers &&
        question.answers
          .filter((answer) => answer.showChapter)
          .forEach((answer) => answer.showChapter && skipRoutes.push(answer.showChapter)),
    )

    return skipRoutes.sort()
  }

  const handleStoreSkipRoutes = () => {
    const selectedAnswersRoutes = [...answers]
      .filter((answer) => answer.showChapter && !answer.skipAll)
      .map((answer) => answer.showChapter)

    const skipRoutes = listOfSkippableRoutes()
    const skipRoutesFromLocalStorage = getSkipRouteStorage()

    // If no answers are set, unskip all chapters from all answers of this question
    if (selectedAnswersRoutes.length === 0) {
      setSkipRouteStorage(skipRoutesFromLocalStorage.filter((sr) => !skipRoutes.includes(sr)))
    } else {
      // If answers are set, update the list of skipped chapters
      let newRoutesToSkip = [...skipRoutesFromLocalStorage]

      skipRoutes.forEach((skipRoute) => {
        const shouldSkip = !selectedAnswersRoutes.includes(skipRoute)

        // Remove selected answers from the list of skipped chapters
        // And unselected answers are added to the list of skipped chapters
        if (shouldSkip) {
          newRoutesToSkip = newRoutesToSkip.concat(skipRoute)
        } else {
          newRoutesToSkip = newRoutesToSkip.filter((sr) => sr !== skipRoute)
        }
      })

      setSkipRouteStorage(Array.from(new Set(newRoutesToSkip)).sort())
    }
  }

  const handleLocalStorage = () => {
    const values: string[] = []

    if (allFormItems && allFormItems.length > 0) {
      allFormItems.forEach((formItem: FormItemType) => values.push(formItem.value))
    }

    if (answers && answers.length > 0) {
      answers.forEach((answer) => answer && values.push(answer.title))
    }

    setLocalStorage(
      {
        routeId: routeId,
        answer: values,
        isConditionalRoute: isConditionalRoute,
        explanation: explanationItem,
      },
      basePath,
    )
  }

  const handleChange = (evt: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const name = evt.target.name
    const value = evt.target.value
    const formItems = allFormItems.map((formItem: FormItemType) => {
      if (formItem.inputName === name) {
        formItem.value = value
      }
      return formItem
    })
    setAllFormItems(formItems)

    return { allFormItems }
  }

  const handleCheckboxClick = (answer: Answer, isConditionalRoute = false, isChecked: boolean) => {
    let answersList = [...answers]

    if (isChecked) {
      answersList = answersList.filter((value) => {
        return value.title !== answer.title
      })
    } else {
      if (answer.deselectOthers) {
        answersList = []
      } else {
        const deselectOthersAnswer = questions.map(
          (question) =>
            question.answers && question.answers.find((answer) => answer.deselectOthers),
        )

        deselectOthersAnswer.length > 0 &&
          deselectOthersAnswer.forEach((answer) => {
            if (answer) {
              const index = answersList.indexOf(answer)
              if (index > -1) answersList.splice(index, 1)
            }
          })
      }
      const skipAll = answersList.find((answer) => answer.skipAll)

      // If the answer has a property named 'skipAll', it deselects/empties all answers.
      if (answer.skipAll) {
        answersList = []
        // If the selected answer does not have a property named 'skipAll', but the answersList does contain one, it will remove that answer from the answersList.
      } else if (!answer.skipAll && skipAll) {
        const index = answersList.indexOf(skipAll)
        index > -1 && answersList.splice(index, 1)
      }

      answersList.push(answer)
    }

    setIsConditionalRoute(isConditionalRoute)
    setAnswers(answersList)

    if (answer.showChapter || answer.skipAll) handleStoreSkipRoutes()
  }

  const handleRadioClick = (answer: Answer, isConditionalRoute = false) => {
    setAnswers([answer]), setIsConditionalRoute(isConditionalRoute)

    handleLocalStorage()
    if (answer.showChapter || answer.skipAll) handleStoreSkipRoutes()
  }

  const handleTextArea = (evt: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const value = evt.target.value
    setExplanationItem(value)
    handleLocalStorage()
  }

  // Add modalLink into modalLinks for backwards compatibility
  const modalLinksToUse: ModalLink[] = [
    ...(modalLinks ? modalLinks : []),
    ...(modalLink
      ? [
          {
            key: 'MODALLINK',
            title: modalHeader,
            link: modalLink,
            content: modalText,
          },
        ]
      : []),
  ]

  return (
    <SlideContainer>
      <Content className="text-formatted">
        <Subtitle>{subtitle}</Subtitle>
        <h1 id={title.replace(/[^A-Z0-9]+/gi, '_')}>{title}</h1>
        <Readspeaker />
        <Description $align={descriptionAlign} dangerouslySetInnerHTML={{ __html: description }} />

        <FormContainer>
          {questions &&
            questions.map((question: QuestionType, index: number) => (
              <div key={question.title || question.placeholder || ''}>
                {question.type === 'textarea' && (
                  <Form.TextArea
                    onChange={handleChange}
                    name={allFormItems[index].inputName}
                    placeholder={question.placeholder || ''}
                    title={question.title}
                    value={allFormItems[index].value || ''}
                    questionId={title.replace(/[^A-Z0-9]+/gi, '_')}
                  />
                )}
                {question.type === 'textfield' && (
                  <Form.TextField
                    onChange={handleChange}
                    name={allFormItems[index].inputName}
                    placeholder={question.placeholder || ''}
                    value={allFormItems[index].value || ''}
                    questionId={title.replace(/[^A-Z0-9]+/gi, '_')}
                  />
                )}
                {question.type === 'multiple' && (
                  <QuestionComponents.Multiple
                    question={question}
                    onSelect={(answer, isConditionalRoute, isChecked) => {
                      handleCheckboxClick(answer, isConditionalRoute, isChecked)
                    }}
                    selectedValues={answers.map((answer) => answer.title)}
                    title={title}
                  />
                )}
                {question.type === 'single' && (
                  <QuestionComponents.Single
                    question={question}
                    onSelect={(answer, isConditionalRoute) => {
                      handleRadioClick(answer, isConditionalRoute)
                    }}
                    selectedValue={answers[0] ? answers[0].title : ''}
                    title={title}
                  />
                )}

                {showTextArea && answers.length !== 0 && (
                  <TextAreaWrapper>
                    {textAreaLabel ? (
                      <Label htmlFor={textAreaLabel.replace(/[^A-Z0-9]+/gi, '_') + index}>
                        {textAreaLabel}
                      </Label>
                    ) : (
                      <HiddenLabel htmlFor={'Je_kunt_hier_vertellen_waarom_' + index}>
                        Je kunt hier vertellen waarom
                      </HiddenLabel>
                    )}
                    <StyledTextArea
                      id={
                        textAreaLabel
                          ? textAreaLabel.replace(/[^A-Z0-9]+/gi, '_') + index
                          : 'Je_kunt_hier_vertellen_waarom_' + index
                      }
                      placeholder="Je kunt hier vertellen waarom."
                      onChange={handleTextArea}
                      value={explanationItem}
                    ></StyledTextArea>
                  </TextAreaWrapper>
                )}
              </div>
            ))}
        </FormContainer>

        {Boolean(modalLinksToUse.length) &&
          modalLinksToUse.map((ml) => (
            <ModalConsumer key={ml.key}>
              {({ openModal }) => (
                <StyledLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    openModal(ml.title, ml.content)
                  }}
                >
                  {ml.link}
                </StyledLink>
              )}
            </ModalConsumer>
          ))}

        {nextLink && (
          <Next>
            <LinkButton onClick={handleLocalStorage} to={nextLink}>
              {button}
            </LinkButton>
          </Next>
        )}
      </Content>
    </SlideContainer>
  )
}

const Content = styled.div`
  text-align: center;
  max-width: 100%;
  width: 100%;

  @media (min-width: ${Breakpoints.md}) {
    width: 700px;
  }
`

const Description = styled.div<{ $align: string | undefined }>`
  margin: 0 0 35px;
  text-align: ${({ $align }) => $align && $align};

  & > h2 {
    font-size: 18px;
    line-height: inherit;
    margin-bottom: 0;

    & + * {
      margin-top: 0;
    }
  }
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;

  & > div {
    margin-right: 20px;
    width: 100%;

    label {
      margin-right: 0;
    }

    fieldset {
      margin-top: 60px;
    }
    &:last-child {
      margin-right: 0;
    }
  }

  @media (min-width: ${Breakpoints.md}) {
    flex-direction: row;
  }

  @media (min-width: ${Breakpoints.lg}) {
    margin-left: -15%;
    width: 130%;
  }
`

const Next = styled.div`
  margin-top: 30px;
  width: 100%;
`

const Subtitle = styled.span`
  font-size: 1rem;
  font-weight: bold;
`

const StyledLink = styled.a`
  border-color: ${({ theme }) => theme.blue};
  color: ${({ theme }) => theme.blue};
  display: inline-block;
  margin-top: 25px;
`

const TextAreaWrapper = styled.div`
  margin: 40px auto 60px;
`

const Label = styled.label`
  font-weight: bold;
  font-size: 1.25rem;
  display: inline-block;
  margin-bottom: 24px;
`

const HiddenLabel = styled.label`
  clip: rect(1px, 1px, 1px, 1px);
  height: 1px;
  overflow: hidden;
  position: absolute;
  width: 1px;
  margin: 0;
`

const StyledTextArea = styled.textarea`
  display: block;
  font-size: 1.125rem;
  min-height: 250px;
  max-width: 604px;
  width: 100%;
  padding: 20px;
  margin: auto;
  font-family: 'Circular', sans-serif;
  border: solid 1px ${({ theme }) => theme.lightGrey};
  box-shadow: 0 4px 13px 0 rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  transition: all 0.3s ease;
`

export default Question
